/**
 * WorldBattleHost — TODOS #25 M2, the in-world duel battle driver.
 *
 * A host OBJECT (not a Phaser.Scene) that runs the FULL interactive
 * battle over the AWAKE WorldScene: the DuelStage battler and the
 * roamer's own OW sprite are the two battlers, the battle chrome
 * (actions / log / status panels / float text) mounts screen-anchored
 * DOM over the punched-in world camera, and ALL combat bookkeeping —
 * choice loop, event playback, HP/status/permadeath flushes, EXP +
 * level-up + LearnedMovesModal, career records, cinematics — comes
 * from adopting BattleScene's three mixins (eventHandlers, cinematics,
 * choiceLoop). Nothing is forked.
 *
 * THE SINGLE CAST BOUNDARY: the mixin functions type `this:
 * BattleScene`. This host is structurally compatible with every field
 * and method they touch — the complete display-model set below (built
 * by grepping every `this.*` in all three mixin files against
 * BattleScene.init()/create()), the scene-facility getters, and the
 * host seams (layout / toScreen / screenScale / applySwitchVisual /
 * battlerBaseScale / exitToWorld / exitToEndRun) — but it is NOT a
 * Scene subclass, so every adoption call routes through one documented
 * cast: the `asScene` getter. Missing a field the mixins read is a
 * runtime crash; keep the set exhaustive when the mixins grow.
 *
 * Ownership: the battler sprite belongs to DuelStage and the wild
 * sprite to RoamerSystem — the host borrows both and NEVER destroys
 * them (teardown only kills battle-born tweens riding them and drops
 * the refs). Chrome handles + keyboard listeners are host-owned and
 * torn down in teardown(). WorldScene.runInWorldDuel owns the intro/
 * outro theater, the follower hide/re-materialize, the duel input
 * regime, and the post-battle map-music restore.
 *
 * Lifecycle: `await host.run()` resolves when the battle exits —
 * exitToWorld (win / flee / non-wipe loss) or exitToEndRun (full
 * wipe, which launches EndRunScene and puts the world to sleep;
 * `endedInEndRun` tells the caller to skip the outro tweens, which
 * would hang forever on a sleeping scene's clock).
 */
import Phaser from 'phaser';
import type { BattleSetup, TurnRequest } from '../battle/types';
import type { InteractiveBattle } from '../battle/runInteractiveBattle';
import type { BattleBagRow } from '../battle/battleBag';
import { preloadBattleFx } from '../battle/hitFx';
import { preloadTypeChart } from '../battle/typeChart';
import { BATTLE_FX_MANIFEST } from '../data/battleFxManifest';
import { runState } from '../state/runState';
import { audio } from '../systems/audio';
import {
  mountBattleStatusPanel,
  type BattleStatusPanelHandle,
} from '../ui/battleStatusPanel';
import {
  mountBattleLog,
  BATTLE_LOG_TOP_ESTIMATE_PX,
  type BattleLogHandle,
} from '../ui/battleLog';
import {
  duelAxis,
  placeDuelBanners,
  type BattlerAnchor,
} from '../ui/duelBannerLayout';
import { mountBattleActions, type BattleActionsHandle } from '../ui/battleActions';
import { mountBattleFloatText, type BattleFloatTextHandle } from '../ui/battleFloatText';
import { OW_IDLE_FRAME, encodedDirection } from '../render/follower';
import { applyOwSkin, restScaleOf } from '../render/duelHdSkin';
import type { Facing } from '../render/followerState';
import { playEvent as playEventImpl, applyStatusTint } from './BattleScene.eventHandlers';
import {
  tweenAttacker as tweenAttackerImpl,
  tweenFaint as tweenFaintImpl,
  runPlayerFaintCinematic as runPlayerFaintCinematicImpl,
  runOpponentDeathFade as runOpponentDeathFadeImpl,
  runVictoryHero as runVictoryHeroImpl,
  runDefeatCinematic as runDefeatCinematicImpl,
} from './BattleScene.cinematics';
import {
  waitForChoicesReady as waitForChoicesReadyImpl,
  signalChoicesReady as signalChoicesReadyImpl,
  onBackClick as onBackClickImpl,
  hideActionButtons as hideActionButtonsImpl,
  onActionButtonClick as onActionButtonClickImpl,
  showMainMenu as showMainMenuImpl,
  showMoveMenu as showMoveMenuImpl,
  showBagMenu as showBagMenuImpl,
  showBagTargetMenu as showBagTargetMenuImpl,
  confirmBagUse as confirmBagUseImpl,
  showSwitchMenu as showSwitchMenuImpl,
  promptAction as promptActionImpl,
  promptForceSwitch as promptForceSwitchImpl,
  runFromBattle as runFromBattleImpl,
  flushSurvivorHpToRunState as flushSurvivorHpToRunStateImpl,
  flushBattleStatusToRunState as flushBattleStatusToRunStateImpl,
  spliceFaintedFromRunState as spliceFaintedFromRunStateImpl,
  run as runImpl,
  appendLog as appendLogImpl,
  applyHpBarType as applyHpBarTypeImpl,
  updateLastAliveCriticalState as updateLastAliveCriticalStateImpl,
  wait as waitImpl,
  refreshPlayerExp as refreshPlayerExpImpl,
} from './BattleScene.choiceLoop';
import {
  capitalize,
  type ActionResult,
  type BagTargetView,
  type BattleScene,
  type BattleSceneData,
} from './BattleScene';

/** The world-scene surface the host drives. Structural (rather than
 *  `import type { WorldScene }`) to keep the host decoupled from the
 *  5k-line scene class — everything here is Phaser.Scene plus the one
 *  projection seam WorldScene exposes publicly. */
export interface DuelWorldScene extends Phaser.Scene {
  worldToScreen(wx: number, wy: number): { x: number; y: number };
}

/** Fraction of a battler's drawn frame height that its art actually
 *  fills, measured up from the feet. The 32 px OW frames carry the
 *  creature in their lower ~2/3 with transparent headroom above, so a
 *  banner hung at the FRAME top floats in empty sky — very visible at
 *  duel zoom, where one world px is six screen px. Taken off the live
 *  sprite's displayHeight rather than a world constant so it tracks
 *  zoom, HD skins (128 px frames at ¼ scale) and the level-up scale
 *  pulse for free. */
const BANNER_ART_FRACTION = 0.68;
/** Screen-px clearance between a banner and its creature's art. */
const BANNER_GAP_PX = 8;
const BANNER_EDGE_PX = 10;
const BANNER_SAFE_TOP_PX = 8;

export interface WorldBattleHostOptions {
  worldScene: DuelWorldScene;
  /** DuelStage's walked-in battler sprite (borrowed — never destroyed
   *  here; outro() owns its death). */
  battler: Phaser.GameObjects.Sprite;
  /** RoamerSystem's wild OW sprite (borrowed — never destroyed here;
   *  consume() owns its death). */
  wildSprite: Phaser.GameObjects.Sprite;
  /** The battler's home tile for the duel (DuelStage.slotTile). Sim
   *  lunges/faints displace the sprite; layout() snaps it back here. */
  slotTile: { x: number; y: number };
  /** The wild's frozen tile (roamer ticking is gated for the whole
   *  engagement). */
  wildTile: { x: number; y: number };
  tileSize: number;
  setup: BattleSetup;
  opponentDexId: number;
  playerDexId: number;
  battleZone: string;
  /** Trainer duels (#26): the trainer NPC's scriptRef. Present →
   *  the shared mixins run their trainer branches (defeat flags,
   *  victory music tables, rival sparring, Brock → EndRun win) AND
   *  |switch|p2 re-skins the opponent battler to the incoming
   *  species' OW sheet (multi-mon parties). Absent → wild rules. */
  trainerScriptRef?: string;
  /** UI-camera hygiene hook: mixin-born world-space GameObjects (the
   *  hit-FX particle emitters playHitFx creates via scene.add) are
   *  reported here so WorldScene can add them to the zoom-1 UI
   *  camera's ignore list. Every other world-space sprite family goes
   *  through syncUiCameraIgnores; these are born inside shared battle
   *  code with no other hook — skipping this re-opens the
   *  cinematic-walker double-render bug class. */
  onWorldObjectSpawned?: (obj: Phaser.GameObjects.GameObject) => void;
  /** Live HD-art preference (the duel taste-gate toggle). Consulted at
   *  every |switch| re-skin so mid-battle switch-ins arrive in the
   *  currently-toggled skin. Absent → always GBA. */
  hdSkins?: () => boolean;
}

export class WorldBattleHost {
  private readonly world: DuelWorldScene;
  private readonly slotTile: { x: number; y: number };
  private readonly wildTile: { x: number; y: number };
  private readonly tileSize: number;
  private readonly onWorldObjectSpawned?: (
    obj: Phaser.GameObjects.GameObject,
  ) => void;
  private readonly hdSkins: () => boolean;

  /** True once the battle routed to EndRunScene over the now-SLEEPING
   *  world (full party wipe). runInWorldDuel's finally consults this
   *  to skip the outro — a sleeping scene's tween clock never ticks,
   *  so awaiting outro() there would hang forever. Reset Run reloads
   *  the page and tears everything down naturally. */
  endedInEndRun = false;

  /** Resolves the public run() promise. Set once per run; consumed by
   *  the exitToWorld / exitToEndRun overrides. */
  private resolveRun?: () => void;
  private tornDown = false;
  /** Keyboard listeners registered on the WORLD's keyboard plugin.
   *  Removed by (event, fn) pair at teardown — BattleScene's blanket
   *  `off('keydown-B')` would also strip WorldScene's own handlers,
   *  since the plugin is shared here (it wasn't in the scene-owned
   *  BattleScene). */
  private keyHandlers: Array<[string, () => void]> = [];

  // ── Display model ────────────────────────────────────────────────
  // The COMPLETE field set BattleScene.init() resets, replicated 1:1
  // (fresh instance per battle, so initializers do the reset). See
  // BattleScene.ts for the per-field rationale docs — semantics are
  // identical; the mixins read/write these directly.
  params: BattleSceneData;
  /** The two battlers. Typed as OW Sprites here (BattleScene declares
   *  Images); every operation the mixins perform — setTexture /
   *  setAlpha / getCenter / displayWidth / tint ops / x/y /
   *  setPosition / active — exists on both. The asScene cast bridges
   *  the nominal difference. */
  playerSprite!: Phaser.GameObjects.Sprite;
  opponentSprite!: Phaser.GameObjects.Sprite;
  opponentStatusPanel?: BattleStatusPanelHandle;
  playerStatusPanel?: BattleStatusPanelHandle;
  /** POST_UPDATE handler that re-lays the Ringside Banners when the
   *  camera changes. Held for exact-pair removal at teardown. */
  private bannerTick?: () => void;
  battleLogHandle?: BattleLogHandle;
  battleActionsHandle?: BattleActionsHandle;
  battleActionsTapDisposer?: () => void;
  battleFloatText?: BattleFloatTextHandle;
  opponentHpPercent = 100;
  playerHpPercent = 100;
  menuMode: 'none' | 'main' | 'moves' | 'switch' | 'bag' | 'bagTarget' = 'none';
  bagRows: BattleBagRow[] = [];
  bagTargets: BagTargetView[] = [];
  pendingBagItemId: string | null = null;
  pendingRequest: TurnRequest | null = null;
  pendingActionResolver?: (action: ActionResult) => void;
  pendingForceSwitchResolver?: (slot: number) => void;
  activeSlot = 1;
  activeId: string | null = null;
  pendingSwitchToId: string | null = null;
  faintedIds = new Set<string>();
  playerFainted = false;
  hpById: Map<string, { current: number; max: number }> = new Map();
  nextEffectiveness: Record<'p1' | 'p2', 'super' | 'resisted' | 'immune' | null> = {
    p1: null,
    p2: null,
  };
  nextCrit: Record<'p1' | 'p2', boolean> = { p1: false, p2: false };
  lastMoveType: Record<'p1' | 'p2', string | null> = { p1: null, p2: null };
  opponentFaints: Array<{ species: string; level: number; killerId: string }> = [];
  choicesReady = false;
  choicesReadyResolver?: () => void;
  battleOver = false;
  battle: InteractiveBattle | null = null;
  /** Trainer duels: |switch|p2 re-skins the opponent battler's OW
   *  sheet (multi-mon parties). Wild duels never swap the roamer's
   *  own texture. */
  private reskinOpponentOnSwitch = false;

  constructor(opts: WorldBattleHostOptions) {
    this.world = opts.worldScene;
    this.playerSprite = opts.battler;
    this.opponentSprite = opts.wildSprite;
    this.slotTile = opts.slotTile;
    this.wildTile = opts.wildTile;
    this.tileSize = opts.tileSize;
    this.onWorldObjectSpawned = opts.onWorldObjectSpawned;
    this.hdSkins = opts.hdSkins ?? (() => false);
    this.reskinOpponentOnSwitch = Boolean(opts.trainerScriptRef);
    // No frozen-world snapshot either way — the world is LIVE behind
    // the chrome. trainerScriptRef routes the shared mixins' trainer
    // branches (flee rules / victory music / rival sparring / Brock).
    this.params = {
      setup: opts.setup,
      opponentDexId: opts.opponentDexId,
      playerDexId: opts.playerDexId,
      battleZone: opts.battleZone,
      trainerScriptRef: opts.trainerScriptRef,
      snapshotHandle: null,
    };
    // v7 hpById seeding + initial active — mirrors BattleScene.init().
    for (const m of runState.getParty()) {
      this.hpById.set(m.id, { current: m.hp.current, max: m.hp.max });
    }
    const first = runState.getParty()[0];
    this.activeId = first?.id ?? null;
    this.playerHpPercent =
      first && first.hp.max > 0
        ? Math.round((first.hp.current / first.hp.max) * 100)
        : 100;
  }

  /** THE single cast boundary (see the module doc). The mixins type
   *  `this: BattleScene`; the host satisfies their full structural
   *  contract but is not a Scene subclass, so every adoption call
   *  routes through this one documented cast — nowhere else. */
  private get asScene(): BattleScene {
    return this as unknown as BattleScene;
  }

  // ── Scene facilities (delegating getters to the live WorldScene) ──
  get tweens(): Phaser.Tweens.TweenManager {
    return this.world.tweens;
  }
  get time(): Phaser.Time.Clock {
    return this.world.time;
  }
  get cameras(): Phaser.Cameras.Scene2D.CameraManager {
    return this.world.cameras;
  }
  get scale(): Phaser.Scale.ScaleManager {
    return this.world.scale;
  }
  /** ScenePlugin — the mixins use it for LearnedMovesModal launches
   *  (battleEnd's overflow ceremony) and our overrides for EndRunScene. */
  get scene(): Phaser.Scenes.ScenePlugin {
    return this.world.scene;
  }
  get textures(): Phaser.Textures.TextureManager {
    return this.world.textures;
  }
  get anims(): Phaser.Animations.AnimationManager {
    return this.world.anims;
  }
  get sound():
    | Phaser.Sound.NoAudioSoundManager
    | Phaser.Sound.HTML5AudioSoundManager
    | Phaser.Sound.WebAudioSoundManager {
    return this.world.sound;
  }
  get load(): Phaser.Loader.LoaderPlugin {
    return this.world.load;
  }
  get input(): Phaser.Input.InputPlugin {
    return this.world.input;
  }

  private addDelegate?: Phaser.GameObjects.GameObjectFactory;
  /** GameObjectFactory delegate. `particles` is the ONE factory method
   *  the mixins reach (playHitFx → scene.add.particles), and its
   *  emitters are world-space objects the zoom-1 UI camera would
   *  double-render — so it's wrapped to report each spawn through
   *  onWorldObjectSpawned. Everything else resolves through the
   *  prototype chain to the real factory. */
  get add(): Phaser.GameObjects.GameObjectFactory {
    if (!this.addDelegate) {
      const base = this.world.add;
      const notify = this.onWorldObjectSpawned;
      if (!notify) {
        this.addDelegate = base;
      } else {
        const wrapped = Object.create(base) as Phaser.GameObjects.GameObjectFactory;
        wrapped.particles = ((
          ...args: Parameters<Phaser.GameObjects.GameObjectFactory['particles']>
        ) => {
          const emitter = base.particles(...args);
          notify(emitter);
          return emitter;
        }) as Phaser.GameObjects.GameObjectFactory['particles'];
        this.addDelegate = wrapped;
      }
    }
    return this.addDelegate;
  }

  // ── Host seams (the duel-specific overrides of BattleScene's) ─────

  /** Scene→screen projection for the DOM float anchors: through the
   *  punched-in world camera (BattleScene's is the identity). */
  toScreen(x: number, y: number): { x: number; y: number } {
    return this.world.worldToScreen(x, y);
  }

  /** Scene-px → screen-px factor for sprite extents in DOM anchor
   *  math: the live world camera zoom (BattleScene returns 1). */
  screenScale(): number {
    return this.world.cameras.main.zoom;
  }

  /** Resting scale of the active p1 battler — the level-up pulse
   *  tweens from/to this base. GBA walkers rest at 1; HD-skinned
   *  battlers at 1/4 (128px frames, same world footprint). Derived
   *  from the texture key so toggle flips and mid-tween reads can't
   *  hand the pulse a stale base and strand an HD battler at 4×. */
  battlerBaseScale(): number {
    return restScaleOf(this.playerSprite.texture.key);
  }

  /** Impact-FX spatial multiplier. The particles are WORLD-space
   *  objects rendered through the punched camera, so their screen
   *  size already picks up the zoom — the zoom term CANCELS out of
   *  the proportion math. What's left is the constant art-size ratio:
   *  32px walkers vs the 64px-at-4x (256px) stills the FX were tuned
   *  against = 1/8. (The first pass multiplied by zoom and the stars
   *  drowned the battlers — phone dogfood round 2.) */
  hitFxScale(): number {
    return 32 / 256;
  }

  /** Swap the visible battler art on a |switch| event.
   *  p1 → re-skin the walker to the incoming member's OW sheet (all
   *  v0.1 party species ship follower sheets; a miss keeps the prior
   *  skin rather than flashing __MISSING), restore alpha, snap to the
   *  slot facing the wild.
   *  p2 → alpha restore ONLY. The roamer's own sprite IS the opponent;
   *  its texture is already right and never swaps (v0.1 wilds are
   *  single-mon — the |switch|p2a only fires at battle start). */
  applySwitchVisual(side: 'p1' | 'p2', species: string): void {
    if (side === 'p1') {
      const b = this.playerSprite;
      if (!b || !b.active) return;
      // applyOwSkin keeps the miss-guard posture (missing sheet → keep
      // the prior skin, never __MISSING) and routes the HD taste-gate
      // pref so a switch-in arrives in the currently-toggled art.
      applyOwSkin(this.world.textures, b, species, this.hdSkins());
      b.setAlpha(1);
      this.snapBattlerToSlot(b);
    } else if (this.opponentSprite && this.opponentSprite.active) {
      if (this.reskinOpponentOnSwitch) {
        // Trainer duel: the incoming party member takes the field —
        // re-skin the duel-owned opponent battler and face it back
        // toward my slot. All trainer-party species ship OW sheets
        // (geodude joined the set 2026-08-03).
        applyOwSkin(
          this.world.textures,
          this.opponentSprite,
          species,
          this.hdSkins(),
        );
        const facingRight = this.slotTile.x > this.wildTile.x;
        this.opponentSprite.setFlipX(facingRight);
        this.opponentSprite.setFrame(
          OW_IDLE_FRAME[
            facingRight || this.slotTile.x < this.wildTile.x
              ? 'left'
              : this.slotTile.y > this.wildTile.y
                ? 'down'
                : 'up'
          ],
        );
      }
      this.opponentSprite.setAlpha(1);
    }
  }

  /** Snap both battlers to their tile anchors — undoing tweenFaint's
   *  +32y/alpha displacement on the battler (the |switch|p1 handler
   *  calls layout() after a force-switch for exactly this). Anchor
   *  convention matches DuelStage/RoamerSystem: origin (0.5, 1) at
   *  ((tile.x + 0.5) * t, (tile.y + 1) * t). */
  layout(): void {
    const t = this.tileSize;
    const b = this.playerSprite;
    if (b && b.active) {
      b.setOrigin(0.5, 1);
      b.setPosition((this.slotTile.x + 0.5) * t, (this.slotTile.y + 1) * t);
      b.setAlpha(1);
    }
    const w = this.opponentSprite;
    if (w && w.active) {
      w.setPosition((this.wildTile.x + 0.5) * t, (this.wildTile.y + 1) * t);
    }
  }

  /** Battle over, world continues: tear the chrome down and resolve
   *  the duel promise. runInWorldDuel's finally owns the outro /
   *  follower re-materialization / input regime / music restore. */
  exitToWorld(): void {
    this.teardown();
    const r = this.resolveRun;
    this.resolveRun = undefined;
    r?.();
  }

  /** Full-wipe path: EndRunScene over the sleeping world. Same data
   *  shape as BattleScene.cinematics.exitToEndRun; the duel path has
   *  no frozen-world snapshot, and EndRunScene's null branch falls
   *  back to the opaque #0E1014 backdrop. Same anti-softlock fallback
   *  (M5 critical gap): a failed launch offers reload rather than a
   *  frozen screen. */
  exitToEndRun(
    mode: 'win' | 'defeat',
    casualties: Array<{ species: string; level: number }>,
  ): void {
    this.endedInEndRun = true;
    this.teardown();
    try {
      this.world.scene.launch('EndRunScene', {
        mode,
        casualties,
        snapshotHandle: null,
      });
      // The run is over — the world sleeps under EndRunScene, whose
      // Reset Run reloads the page and tears both down naturally.
      this.world.scene.sleep();
    } catch (err) {
      console.error('[duel] EndRunScene launch failed:', err);
      const ok = window.confirm(
        mode === 'win'
          ? 'You defeated Brock! Run complete. Reload to start a fresh run?'
          : 'Your party has fallen. Reload to start a fresh run?',
      );
      if (ok) window.location.reload();
    }
    const r = this.resolveRun;
    this.resolveRun = undefined;
    r?.();
  }

  // ── Run lifecycle ─────────────────────────────────────────────────

  /** Drive the whole battle. Resolves when it exits (exitToWorld or
   *  exitToEndRun) — NOT when the choice-loop returns: on the flee
   *  path the sim's event stream never ends (BattleScene leaves the
   *  dangling BattleStream to GC on scene stop; same posture here). */
  async run(): Promise<void> {
    const done = new Promise<void>((resolve) => {
      this.resolveRun = resolve;
    });
    // Wild battle music + type-chart warm — mirrors BattleScene
    // .create()'s wild branch (duels have no trainer overrides).
    void audio.playMusic(this.world, 'wild_battle', { fadeMs: 400 });
    void preloadTypeChart();
    await this.ensureBattleFxLoaded();
    this.createChrome();
    this.initialSync();
    this.layout();
    this.startBannerLayout();
    // Adopt the choice-loop driver. Errors inside it are its own
    // (startInteractiveBattle failures already exit gracefully); this
    // catch is the never-strand-the-world backstop.
    void runImpl.call(this.asScene).catch((err) => {
      console.error('[duel] battle run loop error:', err);
      this.exitToWorld();
    });
    await done;
  }

  /** Battle FX sheets are normally loaded by BattleScene.preload
   *  (preloadBattleFx — the 17 cartridge battle-anim sheets; the
   *  pkmn front/back stills it also loads are NOT needed here, the
   *  battlers stay OW walkers). The world's loader is idle post-boot,
   *  so a manual start() + COMPLETE await covers the first duel;
   *  Phaser's texture cache makes every later one a no-op. */
  private ensureBattleFxLoaded(): Promise<void> {
    const missing = Object.keys(BATTLE_FX_MANIFEST).some(
      (id) => !this.world.textures.exists(`fx-${id}`),
    );
    if (!missing) return Promise.resolve();
    return new Promise<void>((resolve) => {
      preloadBattleFx(this.world.load);
      this.world.load.once(Phaser.Loader.Events.COMPLETE, () => resolve());
      this.world.load.start();
    });
  }

  /** Mount the screen-anchored DOM chrome against the live WorldScene
   *  (the scene arg only wires SLEEP/WAKE visibility) and wire the
   *  action-tap + keyboard dispatch — mirrors BattleScene
   *  .createObjects()'s chrome half + createActionButtons(), minus
   *  dialogueOverlay (unused by the mixins), battleStage and snapshot
   *  (the LIVE world is the stage). */
  private createChrome(): void {
    // Ringside Banners: in a duel the panels are nameplates that ride
    // their creatures, not corner cards (owner ruling 2026-08-03). The
    // classic full-screen path still mounts these same components with
    // the default corner anchor — one component, two anchorings.
    this.opponentStatusPanel = mountBattleStatusPanel(this.world, {
      side: 'opponent',
      anchor: 'field',
    });
    this.playerStatusPanel = mountBattleStatusPanel(this.world, {
      side: 'player',
      anchor: 'field',
    });
    this.battleLogHandle = mountBattleLog(this.world);
    this.battleFloatText = mountBattleFloatText(this.world);
    this.battleActionsHandle = mountBattleActions(this.world);
    this.battleActionsTapDisposer = this.battleActionsHandle.onTap((target) => {
      if (target === 'back') this.onBackClick();
      else this.onActionButtonClick(target);
    });
    // B/ESC → BACK, 1-4 → move slots while the move menu is up — same
    // semantics as BattleScene.createActionButtons(), registered on
    // the WORLD's keyboard plugin and removed by exact (event, fn)
    // pair at teardown (see keyHandlers).
    const kb = this.world.input.keyboard;
    if (kb) {
      const onBack = (): void => this.onBackClick();
      kb.on('keydown-B', onBack);
      this.keyHandlers.push(['keydown-B', onBack]);
      const onEsc = (): void => this.onBackClick();
      kb.on('keydown-ESC', onEsc);
      this.keyHandlers.push(['keydown-ESC', onEsc]);
      for (let i = 1; i <= 4; i++) {
        const slot = i;
        const onNum = (): void => {
          if (this.menuMode === 'moves') this.onActionButtonClick(slot);
        };
        kb.on(`keydown-${i}`, onNum);
        this.keyHandlers.push([`keydown-${i}`, onNum]);
      }
    }
  }

  /** One-shot initial state sync — mirrors the block at the end of
   *  BattleScene.createObjects(): panel names/levels, player HP bar +
   *  numeric + EXP, both HP-bar type tints, the lead's persistent
   *  status tint on the battler, and the last-alive cinematic gate. */
  private initialSync(): void {
    const opp0 = this.params.setup.p2.team[0];
    if (opp0) {
      this.opponentStatusPanel?.setName(opp0.name ?? capitalize(opp0.species));
      this.opponentStatusPanel?.setLevel(opp0.level ?? 1);
      this.applyHpBarType('p2', opp0.species);
    }
    const p0 = this.params.setup.p1.team[0];
    if (p0) {
      this.playerStatusPanel?.setName(p0.name ?? capitalize(p0.species));
      this.playerStatusPanel?.setLevel(p0.level ?? 1);
      const hp = this.activeId ? this.hpById.get(this.activeId) : null;
      if (hp) {
        this.playerStatusPanel?.setHp(hp.max > 0 ? hp.current / hp.max : 0, true);
        this.playerStatusPanel?.setHpNumeric(hp.current, hp.max);
      }
      const member = runState.getParty()[0];
      if (member) this.playerStatusPanel?.setExp(member.exp, member.level);
      this.applyHpBarType('p1', p0.species);
      // A paralyzed/poisoned lead carrying status from a previous
      // battle reads visually from frame 0 (PR 3 EXP 5e).
      if (member) applyStatusTint(this.asScene, this.asScene.playerSprite, member.status);
    }
    this.updateLastAliveCriticalState();
  }

  /** Destroy everything host-owned. Idempotent. The borrowed sprites
   *  are NOT destroyed — battler is DuelStage's, wild is
   *  RoamerSystem's; only their battle-born tweens are killed (the
   *  world TweenManager outlives this battle, unlike BattleScene's
   *  scene-scoped one, so a paralysis flicker loop would otherwise
   *  ride the sprite forever). */
  private teardown(): void {
    if (this.tornDown) return;
    this.tornDown = true;
    const kb = this.world.input.keyboard;
    for (const [event, fn] of this.keyHandlers) kb?.off(event, fn);
    this.keyHandlers = [];
    this.battleActionsTapDisposer?.();
    this.battleActionsTapDisposer = undefined;
    this.battleActionsHandle?.destroy();
    this.battleActionsHandle = undefined;
    this.opponentStatusPanel?.destroy();
    this.opponentStatusPanel = undefined;
    this.playerStatusPanel?.destroy();
    this.playerStatusPanel = undefined;
    this.battleLogHandle?.destroy();
    this.battleLogHandle = undefined;
    this.battleFloatText?.destroy();
    this.battleFloatText = undefined;
    if (this.bannerTick) {
      this.world.events.off(Phaser.Scenes.Events.POST_UPDATE, this.bannerTick);
      this.bannerTick = undefined;
    }
    // Defensive cinematic body-class cleanup — mirrors BattleScene's
    // SHUTDOWN block so an exit mid-cinematic can't leak world desat.
    const gameEl = document.getElementById('game');
    gameEl?.classList.remove('is-battle-fainting');
    gameEl?.classList.remove('is-last-alive-critical');
    gameEl?.classList.remove('is-super-effective');
    gameEl?.classList.remove('is-battle-defeat');
    if (this.playerSprite) this.world.tweens.killTweensOf(this.playerSprite);
    if (this.opponentSprite) this.world.tweens.killTweensOf(this.opponentSprite);
    // Drop the borrowed refs (ownership note above). The cast mirrors
    // BattleScene's posture — the fields are definite-assigned for the
    // mixins' benefit and no battle code runs past teardown.
    this.playerSprite = undefined as unknown as Phaser.GameObjects.Sprite;
    this.opponentSprite = undefined as unknown as Phaser.GameObjects.Sprite;
  }

  /** Ringside Banner layout. The two home tiles are fixed for the whole
   *  duel and the camera is locked to their midpoint, so this is not a
   *  per-frame tracker: it runs once now and again only when the CAMERA
   *  actually changes (pinch zoom, resize, the intro punch settling).
   *  Gating on camera state rather than on a list of events is
   *  deliberate — an event list is a standing invitation for the next
   *  camera move to strand a banner somewhere. */
  private startBannerLayout(): void {
    const cam = this.world.cameras.main;
    let lastZoom = -1;
    let lastX = Number.NaN;
    let lastY = Number.NaN;
    let lastW = -1;
    const tick = (): void => {
      if (
        cam.zoom === lastZoom &&
        cam.worldView.x === lastX &&
        cam.worldView.y === lastY &&
        cam.width === lastW
      ) {
        return;
      }
      lastZoom = cam.zoom;
      lastX = cam.worldView.x;
      lastY = cam.worldView.y;
      lastW = cam.width;
      this.layoutBanners();
    };
    tick();
    this.world.events.on(Phaser.Scenes.Events.POST_UPDATE, tick);
    this.bannerTick = tick;
  }

  private layoutBanners(): void {
    const mine = this.playerStatusPanel;
    const opp = this.opponentStatusPanel;
    if (!mine || !opp) return;
    const t = this.tileSize;
    const cam = this.world.cameras.main;
    // Anchored to the HOME TILE, not the live sprite: lunges and
    // knockbacks must not drag the nameplate around. Only the head
    // clearance comes off the sprite, and only its height.
    const anchorFor = (
      tile: { x: number; y: number },
      sprite: Phaser.GameObjects.Sprite | undefined,
    ): BattlerAnchor => {
      const feetWorldX = (tile.x + 0.5) * t;
      const feetWorldY = (tile.y + 1) * t;
      const feet = this.toScreen(feetWorldX, feetWorldY);
      const artWorldH = (sprite?.displayHeight ?? t * 2) * BANNER_ART_FRACTION;
      const head = this.toScreen(feetWorldX, feetWorldY - artWorldH);
      return { centerX: feet.x, feetY: feet.y, headY: head.y };
    };
    const layout = placeDuelBanners({
      axis: duelAxis(this.slotTile, this.wildTile),
      a: anchorFor(this.slotTile, this.playerSprite),
      b: anchorFor(this.wildTile, this.opponentSprite),
      aBox: mine.measure(),
      bBox: opp.measure(),
      viewportW: cam.width,
      safeTop: BANNER_SAFE_TOP_PX,
      // The banners live above the battle log; the log's top edge is
      // derived from the action stack, so this tracks any bottom-chrome
      // change automatically.
      safeBottom: cam.height - (BATTLE_LOG_TOP_ESTIMATE_PX + 12),
      gap: BANNER_GAP_PX,
      edge: BANNER_EDGE_PX,
    });
    mine.setFieldPlacement(layout.a);
    opp.setFieldPlacement(layout.b);
  }

  // ── Duel-local helpers ───────────────────────────────────────────

  /** Re-anchor the battler on its slot tile and face the wild (idle
   *  frame + flipX per the TaTaTaZJJ left-facing sheet convention). */
  private snapBattlerToSlot(b: Phaser.GameObjects.Sprite): void {
    const t = this.tileSize;
    b.setOrigin(0.5, 1);
    b.setPosition((this.slotTile.x + 0.5) * t, (this.slotTile.y + 1) * t);
    const dx = this.wildTile.x - this.slotTile.x;
    const dy = this.wildTile.y - this.slotTile.y;
    const facing: Facing =
      Math.abs(dx) >= Math.abs(dy)
        ? dx >= 0
          ? 'right'
          : 'left'
        : dy >= 0
          ? 'down'
          : 'up';
    b.setFlipX(facing === 'right');
    b.setFrame(OW_IDLE_FRAME[encodedDirection(facing)]);
  }

  // ── Mixin adoption delegators (1:1 with BattleScene's) ───────────

  waitForChoicesReady(): Promise<void> {
    return waitForChoicesReadyImpl.call(this.asScene);
  }

  signalChoicesReady(): void {
    return signalChoicesReadyImpl.call(this.asScene);
  }

  onBackClick(): void {
    return onBackClickImpl.call(this.asScene);
  }

  hideActionButtons(): void {
    return hideActionButtonsImpl.call(this.asScene);
  }

  onActionButtonClick(slot: number): void {
    return onActionButtonClickImpl.call(this.asScene, slot);
  }

  showMainMenu(): void {
    return showMainMenuImpl.call(this.asScene);
  }

  showMoveMenu(): void {
    return showMoveMenuImpl.call(this.asScene);
  }

  showBagMenu(): void {
    return showBagMenuImpl.call(this.asScene);
  }

  showBagTargetMenu(): void {
    return showBagTargetMenuImpl.call(this.asScene);
  }

  confirmBagUse(slot: number): void {
    return confirmBagUseImpl.call(this.asScene, slot);
  }

  showSwitchMenu(): void {
    return showSwitchMenuImpl.call(this.asScene);
  }

  promptAction(): Promise<ActionResult> {
    return promptActionImpl.call(this.asScene);
  }

  promptForceSwitch(): Promise<number> {
    return promptForceSwitchImpl.call(this.asScene);
  }

  async runFromBattle(): Promise<void> {
    return runFromBattleImpl.call(this.asScene);
  }

  flushSurvivorHpToRunState(): void {
    return flushSurvivorHpToRunStateImpl.call(this.asScene);
  }

  flushBattleStatusToRunState(battle: InteractiveBattle): void {
    return flushBattleStatusToRunStateImpl.call(this.asScene, battle);
  }

  spliceFaintedFromRunState(): void {
    return spliceFaintedFromRunStateImpl.call(this.asScene);
  }

  async playEvent(event: Parameters<typeof playEventImpl>[0]): Promise<void> {
    return playEventImpl.call(this.asScene, event);
  }

  appendLog(text: string): void {
    return appendLogImpl.call(this.asScene, text);
  }

  applyHpBarType(side: 'p1' | 'p2', species: string): void {
    return applyHpBarTypeImpl.call(this.asScene, side, species);
  }

  tweenAttacker(side: 'p1' | 'p2'): Promise<void> {
    return tweenAttackerImpl.call(this.asScene, side);
  }

  tweenFaint(sprite: Phaser.GameObjects.Image): Promise<void> {
    return tweenFaintImpl.call(this.asScene, sprite);
  }

  updateLastAliveCriticalState(): void {
    return updateLastAliveCriticalStateImpl.call(this.asScene);
  }

  async runPlayerFaintCinematic(lostName: string): Promise<void> {
    return runPlayerFaintCinematicImpl.call(this.asScene, lostName);
  }

  async runOpponentDeathFade(sprite: Phaser.GameObjects.Image): Promise<void> {
    return runOpponentDeathFadeImpl.call(this.asScene, sprite);
  }

  async runVictoryHero(): Promise<void> {
    return runVictoryHeroImpl.call(this.asScene);
  }

  async runDefeatCinematic(): Promise<void> {
    return runDefeatCinematicImpl.call(this.asScene);
  }

  wait(ms: number): Promise<void> {
    return waitImpl.call(this.asScene, ms);
  }

  refreshPlayerExp(species: string): void {
    return refreshPlayerExpImpl.call(this.asScene, species);
  }
}
